import React, { useReducer, useRef } from 'react';
import classNames from 'classnames';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { Theme as MaterialUITheme } from '@rjsf/material-ui';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../common/Button';
import Backdrop from '../backdrop/Backdrop';
import { useGenericDialog } from './useGenericDialog';
import { fields, templates, widgets } from '../submission/templates';
import LoadingPreview from '../submission/LoadingPreview';
import withTheme from '../submission/rjsf/withTheme';
import { initialDraftState, reducer } from '../draft/draftReducer';
import { SubmissionContextProvider } from '../submission/context/SubmissionContextProvider';
import { GenericDialogActionsConfig } from './GenericDialogActionsConfig';
import { dataTestId } from '../../shared/utils/versionUtils';
import { selectVersion } from '../../redux/features/configuration/configuration.slice';

const MuiForm = withTheme(MaterialUITheme);

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  form: {},
}));

export function GenericDialog() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedVersion = useSelector(selectVersion);

  const formRef = useRef(null);
  const [, dispatchDraftState] = useReducer(reducer, initialDraftState);
  const {
    dialogConfig,
    isOpen,
    isLoading,
    validationSchema,
    uiSchema,
    submission,
    isValid,
    onValidate,
    onSectionChange,
  } = useGenericDialog();
  const classes = useStyles(dialogConfig.options?.style);
  const showForm = !isEmpty(validationSchema) && !isEmpty(uiSchema);

  return (
    <Dialog
      open={isOpen}
      fullWidth
      maxWidth="md"
      {...dataTestId({
        value: `dialog-${dialogConfig.id}-${dialogConfig.procedure?.id}-${selectedVersion.value}`,
      })}
    >
      <DialogTitle>
        <Typography
          component="h2"
          variant="h2"
          {...dataTestId({
            value: `dialog-title-${dialogConfig.id}-${dialogConfig.procedure?.id}-${selectedVersion.value}`,
          })}
        >
          {dialogConfig.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="subtitle1"
          component="span"
          gutterBottom
          {...dataTestId({
            value: `dialog-description-${dialogConfig.id}-${dialogConfig.procedure?.id}-${selectedVersion.value}`,
          })}
        >
          {dialogConfig.description}
        </Typography>

        {isLoading && <Backdrop blocking={true} />}

        {showForm && (
          <div
            {...dataTestId({
              value: `dialog-form-${dialogConfig.id}-${dialogConfig.procedure?.id}-${selectedVersion.value}`,
            })}
          >
            <SubmissionContextProvider>
              <MuiForm
                ref={formRef}
                translation={t}
                className={classNames(classes.form, 'rjsf')}
                idPrefix={dialogConfig.id}
                id={dialogConfig.id}
                schema={validationSchema}
                uiSchema={uiSchema}
                fields={fields}
                widgets={widgets}
                templates={templates}
                formData={submission}
                formContext={{
                  classes,
                  dispatchDraftState,
                  schema: validationSchema,
                  submission,
                }}
                onChange={onValidate}
                onBlur={onValidate}
                onSectionChange={onSectionChange}
                disabled={false}
                liveValidate={false}
                showErrorList={false}
                noHtml5Validate
                extraErrors={{}}
                /* eslint-disable react/no-children-prop */
                children={<br />}
              />
            </SubmissionContextProvider>
          </div>
        )}

        {!showForm && <LoadingPreview />}
      </DialogContent>

      <DialogActions>
        {dialogConfig.actions?.map(({ title, color, needsValidation, id }) => (
          <Button
            key={id}
            color={color}
            disabled={needsValidation ? !isValid : false}
            onClick={() => dispatch(GenericDialogActionsConfig[id](submission))}
            {...dataTestId({
              value: `dialog-action-${id}-${dialogConfig.id}-${dialogConfig.procedure?.id}-${selectedVersion.value}`,
            })}
          >
            {title}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
}
